import Phaser from 'phaser';
import { Entity, EntityConfig } from './Entity';
import { PathingSystem } from '../systems/PathingSystem';
import { TowerType } from './Tower';
import { MonsterMovementType } from './Monster';

// ============================================
// Boss Entity
// Based on tower_leveling_and_bosses.md
// ============================================

export type BossType =
  | 'brute_boss'    // Default/first boss - slow ground
  | 'bronze_idol'   // Easy boss - slow ground, arrow resist
  | 'earth_tower'   // Medium boss - faster, very high HP
  | 'snail_beast'   // Hard boss - armor, fire resist
  | 'giant_bug';    // Special boss - phase change (air → ground)

export type DamageType = 'physical' | 'energy' | 'elemental';

export interface BossPhase {
  movementType: MonsterMovementType;
  hpThreshold: number;   // Switch when HP drops below this % (0-1)
  speed: number;
  color?: number;
}

export interface BossResists {
  physical: number;    // Arrows, cannon
  energy: number;      // Laser, tesla
  elemental: number;   // Fire, ice
}

export interface BossStats {
  hp: number;
  damage: number;       // Damage per villager eat
  eatInterval: number;  // Seconds between eats when at base
  color: number;
  size: number;
  phases: BossPhase[];
  resists: BossResists;
}

const BOSS_STATS: Record<BossType, BossStats> = {
  brute_boss: {
    hp: 800,     // 2x (was 400)
    damage: 1,
    eatInterval: 1.2,
    color: 0x8b4513,  // Saddle brown
    size: 48,
    phases: [
      { movementType: 'ground', hpThreshold: 0, speed: 22 }
    ],
    resists: { physical: 0, energy: 0, elemental: 0 },
  },
  bronze_idol: {
    hp: 1000,    // 2x (was 500)
    damage: 1,
    eatInterval: 1.0,
    color: 0xcd7f32,  // Bronze
    size: 50,
    phases: [
      { movementType: 'ground', hpThreshold: 0, speed: 25 }
    ],
    resists: { physical: 0.3, energy: 0, elemental: 0 },
  },
  earth_tower: {
    hp: 1600,    // 2x (was 800)
    damage: 1,
    eatInterval: 0.8,
    color: 0x8b4513,  // Brown
    size: 55,
    phases: [
      { movementType: 'ground', hpThreshold: 0, speed: 35 }
    ],
    resists: { physical: 0, energy: 0.2, elemental: 0.1 },
  },
  snail_beast: {
    hp: 2000,    // 2x (was 1000)
    damage: 2,
    eatInterval: 1.2,
    color: 0x556b2f,  // Dark olive
    size: 60,
    phases: [
      { movementType: 'ground', hpThreshold: 0, speed: 30 }
    ],
    resists: { physical: 0.15, energy: 0, elemental: 0.2 },
  },
  giant_bug: {
    hp: 1400,    // 2x (was 700)
    damage: 1,
    eatInterval: 1.0,
    color: 0x4b0082,  // Indigo
    size: 72,    // 30% larger than 55
    phases: [
      { movementType: 'ground', hpThreshold: 0.6, speed: 45, color: 0x4b0082 },   // Start walking, fly at 60%
      { movementType: 'air', hpThreshold: 0.35, speed: 62, color: 0x9400d3 },     // Flying, land at 35%
      { movementType: 'ground', hpThreshold: 0, speed: 45, color: 0x4b0082 }      // Walking to death
    ],
    resists: { physical: 0.15, energy: 0, elemental: 0.1 },
  },
};

// Map tower types to damage types
const TOWER_DAMAGE_TYPES: Record<TowerType, DamageType> = {
  arrow: 'physical',
  cannon: 'physical',
  antiair: 'physical',
  mortar: 'physical',
  tesla: 'energy',
  laser: 'energy',
  fire: 'elemental',
  ice: 'elemental',
  hive: 'physical',
  lightning: 'energy',
  gem: 'physical',
  trap: 'energy',
};

export type BossState = 'moving' | 'at_base' | 'dead';

export interface BossConfig extends Omit<EntityConfig, 'type'> {
  bossType: BossType;
  pathId: string;
  routeId?: string;
  hpOverride?: number;
  speedMultiplier?: number;
}

export class Boss extends Entity {
  public readonly bossType: BossType;
  private stats: BossStats;
  private pathId: string;
  private routeId: string;
  private pathProgress: number = 0;

  private hp: number;
  private maxHp: number;
  private currentPhase: number = 0;
  private state: BossState = 'moving';

  // Eating at base
  private eatTimer: number = 0;

  // Visuals
  private body: Phaser.GameObjects.Ellipse | Phaser.GameObjects.Image;
  private shadow: Phaser.GameObjects.Container;

  // Image-based animation (multi-frame bosses)
  private useImage: boolean = false;
  private animationFrame: number = 0;
  private animationFrameCount: number = 1;
  private animationTimer: number = 0;
  private static readonly BOSS_ANIMATION_INTERVAL_MS: number = 280; // Slower than monsters (bigger enemy)

  // Giant bug: 4-state animation (walking, walk_to_fly, flying, fly_to_walk)
  private giantBugAnimState: 'walking' | 'walk_to_fly' | 'flying' | 'fly_to_walk' = 'walking';
  private static readonly GIANT_BUG_FRAME_COUNTS = {
    walking: 7,
    walk_to_fly: 3,
    flying: 4,
    fly_to_walk: 5,
  } as const;

  // Callbacks
  public onReachedBase?: () => void;
  public onDeath?: () => void;
  public onEatVillager?: (damage: number) => void;
  public onPhaseChange?: (phase: number, movementType: MonsterMovementType) => void;

  constructor(scene: Phaser.Scene, config: BossConfig) {
    super(scene, { ...config, type: 'boss' });

    this.bossType = config.bossType;
    this.pathId = config.pathId;
    this.routeId = config.routeId || 'main';
    const rawStats = BOSS_STATS[config.bossType];
    if (!rawStats?.phases?.length) {
      console.error(`[Boss] Unknown or invalid boss type: "${config.bossType}", falling back to brute_boss`);
      this.stats = { ...BOSS_STATS.brute_boss };
    } else {
      this.stats = { ...rawStats };
    }
    if (config.hpOverride != null) {
      this.stats.hp = config.hpOverride;
    }
    if (config.speedMultiplier != null && this.stats.phases) {
      this.stats.phases = this.stats.phases.map(p => ({ ...p, speed: p.speed * config.speedMultiplier! }));
    }
    this.hp = this.stats.hp;
    this.maxHp = this.stats.hp;

    // Create visuals: use image if boss has multi-frame art, else ellipse
    const size = this.stats.size;
    const phaseColor = this.stats.phases[0].color || this.stats.color;

    // Giant bug uses 4 animation sets; others use boss-{type}-1, boss-{type}-2, ...
    const firstFrameKey = this.bossType === 'giant_bug'
      ? 'boss-giant_bug-walking-1'
      : `boss-${this.bossType}-1`;

    if (scene.textures.exists(firstFrameKey)) {
      this.useImage = true;
      if (this.bossType === 'giant_bug') {
        this.giantBugAnimState = 'walking'; // Start on ground (phase 0)
        this.animationFrame = 0;
        this.animationFrameCount = Boss.GIANT_BUG_FRAME_COUNTS.walking;
      } else {
        this.animationFrameCount = 1;
        while (scene.textures.exists(`boss-${this.bossType}-${this.animationFrameCount + 1}`)) {
          this.animationFrameCount++;
        }
      }
      const img = scene.add.image(0, 0, firstFrameKey);
      const scale = (2 * size * 1.8) / Math.max(img.width, img.height); // 2x bigger for boss images
      img.setScale(scale);
      this.body = img;
    } else {
      this.body = scene.add.ellipse(0, 0, size, size * 0.9, phaseColor);
      (this.body as Phaser.GameObjects.Ellipse).setStrokeStyle(3, 0x9400d3);  // Purple stroke for bosses
    }

    // Shadow - fuzzy ellipses at bottom of boss (offset varies by boss type)
    const bodyBottomY = this.body instanceof Phaser.GameObjects.Image
      ? (this.body as Phaser.GameObjects.Image).displayHeight / 2
      : size * 0.45;
    const shadowOffset = this.bossType === 'snail_beast' ? 60 : this.bossType === 'earth_tower' ? 48 : 40;
    this.shadow = scene.add.container(0, bodyBottomY - shadowOffset);
    const shadowLayers = [
      { width: size * 3.2, height: size * 0.8, alpha: 0.06 },
      { width: size * 2.9, height: size * 0.72, alpha: 0.08 },
      { width: size * 2.6, height: size * 0.65, alpha: 0.1 },
      { width: size * 2.3, height: size * 0.58, alpha: 0.12 },
      { width: size * 2.0, height: size * 0.5, alpha: 0.14 },
      { width: size * 1.5, height: size * 0.38, alpha: 0.18 },
    ];
    shadowLayers.forEach(layer => {
      const ellipse = scene.add.ellipse(0, 0, layer.width, layer.height, 0x000000, layer.alpha);
      this.shadow.add(ellipse);
    });

    this.container.add([this.shadow, this.body]);

    // Depth updated each frame via updateDepth() for correct overlap with trees/towers
  }

  /** Bottom of boss visual for depth sorting (lower on screen = in front). Boss images often have empty space below the character, so cap extension to stats.size to avoid rendering over trees. */
  protected getDepthY(): number {
    const h = (this.body as Phaser.GameObjects.Components.ComputedSize).displayHeight ?? this.stats.size;
    const feetOffset = Math.min(h / 2, this.stats.size);
    return this.container.y + feetOffset;
  }

  update(delta: number): void {
    if (!this.active || this.destroyed) return;

    switch (this.state) {
      case 'moving':
        this.updateMovement(delta);
        break;
      case 'at_base':
        this.updateAtBase(delta);
        break;
    }

    if (this.useImage) {
      this.updateBossAnimation(delta);
    }
    this.updateDepth();
  }

  private updateBossAnimation(delta: number): void {
    if (!this.useImage || !(this.body instanceof Phaser.GameObjects.Image)) return;

    if (this.bossType === 'giant_bug') {
      this.updateGiantBugAnimation(delta);
      return;
    }

    if (this.animationFrameCount <= 0) return;
    this.animationTimer += delta;
    if (this.animationTimer >= Boss.BOSS_ANIMATION_INTERVAL_MS) {
      this.animationTimer = 0;
      this.animationFrame = (this.animationFrame + 1) % this.animationFrameCount;
      const frameKey = `boss-${this.bossType}-${this.animationFrame + 1}`;
      if (this.scene.textures.exists(frameKey)) {
        this.body.setTexture(frameKey);
      }
    }
  }

  private updateGiantBugAnimation(delta: number): void {
    const frameCount = Boss.GIANT_BUG_FRAME_COUNTS[this.giantBugAnimState];

    this.animationTimer += delta;
    if (this.animationTimer >= Boss.BOSS_ANIMATION_INTERVAL_MS) {
      this.animationTimer = 0;
      this.animationFrame++;

      if (this.animationFrame >= frameCount) {
        // Transition finished or loop wrap
        if (this.giantBugAnimState === 'walk_to_fly') {
          this.giantBugAnimState = 'flying';
          this.animationFrame = 0;
        } else if (this.giantBugAnimState === 'fly_to_walk') {
          this.giantBugAnimState = 'walking';
          this.animationFrame = 0;
        } else {
          this.animationFrame = 0; // Loop walking or flying
        }
      }
    }

    const frameKey = `boss-giant_bug-${this.giantBugAnimState}-${this.animationFrame + 1}`;
    if (this.scene.textures.exists(frameKey)) {
      (this.body as Phaser.GameObjects.Image).setTexture(frameKey);
    }
  }

  private updateMovement(delta: number): void {
    const phase = this.stats.phases[this.currentPhase];
    const speed = phase.speed;
    this.pathProgress += (speed * delta) / 1000;

    // Get position from pathing system
    const pos = PathingSystem.getPositionAlongPath(this.pathId, this.pathProgress, this.routeId);

    if (pos) {
      this.setPosition(pos.x, pos.y);

      // Face movement direction (sprites aim right by default; flip when moving left)
      if (this.useImage && this.body instanceof Phaser.GameObjects.Image) {
        const posAhead = PathingSystem.getPositionAlongPath(this.pathId, this.pathProgress + 0.5, this.routeId);
        if (posAhead) {
          const dx = posAhead.x - pos.x;
          this.body.setFlipX(dx < 0);
        }
      }

      // Air bosses float higher
      if (phase.movementType === 'air') {
        this.container.y -= 15;
      }

      // Check if reached end of path
      const pathLength = PathingSystem.getPathLength(this.pathId, this.routeId);
      if (this.pathProgress >= pathLength) {
        this.reachedBase();
      }
    }
  }

  private updateAtBase(delta: number): void {
    // Eat villagers over time
    this.eatTimer += delta / 1000;

    if (this.eatTimer >= this.stats.eatInterval) {
      this.eatTimer = 0;
      this.eatVillager();
    }
  }

  private eatVillager(): void {
    console.log(`[Boss] ${this.bossType} eating villager`);
    this.onEatVillager?.(this.stats.damage);

    // Visual effect
    this.showEatEffect();
  }

  private showEatEffect(): void {
    if (this.useImage && this.body instanceof Phaser.GameObjects.Image) {
      this.body.setTint(0xff0000);
      this.scene.time.delayedCall(100, () => {
        if (!this.destroyed) (this.body as Phaser.GameObjects.Image).clearTint();
      });
    } else if (this.body instanceof Phaser.GameObjects.Ellipse) {
      this.body.setFillStyle(0xff0000);
      this.scene.time.delayedCall(100, () => {
        if (!this.destroyed) {
          const phase = this.stats.phases[this.currentPhase];
          (this.body as Phaser.GameObjects.Ellipse).setFillStyle(phase.color || this.stats.color);
        }
      });
    }
  }

  private reachedBase(): void {
    if (this.state === 'at_base') return;

    console.log(`[Boss] ${this.bossType} reached base`);
    this.state = 'at_base';
    this.onReachedBase?.();
  }

  /**
   * Take damage with resistance calculation
   * Returns { damageDealt, killed } for tower XP tracking
   */
  takeDamage(amount: number, source?: TowerType): { damageDealt: number; killed: boolean } {
    if (this.destroyed || this.state === 'dead') return { damageDealt: 0, killed: false };

    let actualDamage = amount;

    // Apply resistance based on damage type
    if (source) {
      const damageType = TOWER_DAMAGE_TYPES[source];
      const resist = this.stats.resists[damageType] || 0;
      actualDamage *= (1 - resist);
    }

    this.hp -= actualDamage;

    // Flash effect + bounce (tint for Image, fill for Ellipse)
    if (this.useImage && this.body instanceof Phaser.GameObjects.Image) {
      this.body.setTint(0xffffff);
      this.scene.time.delayedCall(50, () => {
        if (!this.destroyed) (this.body as Phaser.GameObjects.Image).clearTint();
      });
    } else if (this.body instanceof Phaser.GameObjects.Ellipse) {
      this.body.setFillStyle(0xffffff);
      this.scene.time.delayedCall(50, () => {
        if (!this.destroyed) {
          const phase = this.stats.phases[this.currentPhase];
          (this.body as Phaser.GameObjects.Ellipse).setFillStyle(phase.color || this.stats.color);
        }
      });
    }

    // Check for phase change
    this.checkPhaseChange();

    // Check for death
    const killed = this.hp <= 0;
    if (killed) {
      this.die();
    }
    
    return { damageDealt: actualDamage, killed };
  }

  private checkPhaseChange(): void {
    const hpPercent = this.hp / this.maxHp;

    for (let i = this.currentPhase + 1; i < this.stats.phases.length; i++) {
      const phase = this.stats.phases[i];
      if (hpPercent <= phase.hpThreshold) {
        this.transitionToPhase(i);
        break;
      }
    }
  }

  private transitionToPhase(phaseIndex: number): void {
    if (phaseIndex === this.currentPhase) return;

    const oldPhase = this.stats.phases[this.currentPhase];
    const newPhase = this.stats.phases[phaseIndex];

    console.log(`[Boss] ${this.bossType} transitioning to phase ${phaseIndex + 1} (${oldPhase.movementType} → ${newPhase.movementType})`);

    this.currentPhase = phaseIndex;

    // Giant bug: trigger transition animation (walk_to_fly or fly_to_walk)
    if (this.bossType === 'giant_bug' && this.useImage) {
      if (oldPhase.movementType === 'ground' && newPhase.movementType === 'air') {
        this.giantBugAnimState = 'walk_to_fly';
      } else if (oldPhase.movementType === 'air' && newPhase.movementType === 'ground') {
        this.giantBugAnimState = 'fly_to_walk';
      }
      this.animationFrame = 0;
      this.animationTimer = 0;
    }

    // Update visuals (tint for Image, fill for Ellipse) - skip for giant_bug (uses image colors)
    if (newPhase.color && this.bossType !== 'giant_bug') {
      if (this.useImage && this.body instanceof Phaser.GameObjects.Image) {
        this.body.setTint(newPhase.color);
      } else if (this.body instanceof Phaser.GameObjects.Ellipse) {
        this.body.setFillStyle(newPhase.color);
      }
    }

    // Show phase transition effect
    this.showPhaseTransition(oldPhase.movementType, newPhase.movementType);

    // Callback
    this.onPhaseChange?.(phaseIndex, newPhase.movementType);
  }

  private showPhaseTransition(_from: MonsterMovementType, to: MonsterMovementType): void {
    // Dramatic transition effect
    const particles: Phaser.GameObjects.Arc[] = [];
    for (let i = 0; i < 12; i++) {
      const particle = this.scene.add.circle(
        this._x,
        this._y,
        8,
        to === 'ground' ? 0x8b4513 : 0x9400d3
      );
      particle.setDepth(100);
      particles.push(particle);

      const angle = (i / 12) * Math.PI * 2;
      const dist = 80;

      this.scene.tweens.add({
        targets: particle,
        x: this._x + Math.cos(angle) * dist,
        y: this._y + Math.sin(angle) * dist,
        alpha: 0,
        duration: 500,
        onComplete: () => particle.destroy(),
      });
    }

    // Show transition text
    const text = this.scene.add.text(this._x, this._y - 60, to === 'ground' ? 'GROUNDED!' : 'FLYING!', {
      fontSize: '16px',
      color: '#ffffff',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3,
    });
    text.setOrigin(0.5);
    text.setDepth(100);

    this.scene.tweens.add({
      targets: text,
      y: this._y - 100,
      alpha: 0,
      duration: 1500,
      onComplete: () => text.destroy(),
    });
  }

  private die(): void {
    if (this.destroyed || this.state === 'dead') return;

    this.state = 'dead';
    console.log(`[Boss] ${this.bossType} defeated!`);

    // Epic death effect
    this.showDeathEffect();

    // Callback
    this.onDeath?.();

    // Delayed destroy for effect
    this.scene.time.delayedCall(500, () => {
      this.destroy();
    });
  }

  private showDeathEffect(): void {
    const x = this._x;
    const y = this._y;

    // Multiple particle bursts
    for (let burst = 0; burst < 3; burst++) {
      this.scene.time.delayedCall(burst * 150, () => {
        for (let i = 0; i < 16; i++) {
          const particle = this.scene.add.circle(
            x + Phaser.Math.Between(-20, 20),
            y + Phaser.Math.Between(-20, 20),
            6,
            this.stats.color
          );
          particle.setDepth(100);

          const angle = (i / 16) * Math.PI * 2;
          const dist = 60 + burst * 30;

          this.scene.tweens.add({
            targets: particle,
            x: x + Math.cos(angle) * dist,
            y: y + Math.sin(angle) * dist,
            alpha: 0,
            scale: 0,
            duration: 400,
            ease: 'Power2',
            onComplete: () => particle.destroy(),
          });
        }
      });
    }

    // Body shrinks
    this.scene.tweens.add({
      targets: this.body,
      scaleX: 0,
      scaleY: 0,
      alpha: 0,
      duration: 500,
    });

    // Victory text
    const text = this.scene.add.text(x, y - 50, 'BOSS DEFEATED!', {
      fontSize: '20px',
      color: '#ffd700',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 4,
    });
    text.setOrigin(0.5);
    text.setDepth(100);

    this.scene.tweens.add({
      targets: text,
      y: y - 100,
      alpha: 0,
      duration: 2000,
      onComplete: () => text.destroy(),
    });
  }

  // ============================================
  // Getters
  // ============================================

  getHp(): number { return this.hp; }
  getMaxHp(): number { return this.maxHp; }
  getHpPercent(): number { return this.hp / this.maxHp; }
  getCurrentPhase(): number { return this.currentPhase; }
  getState(): BossState { return this.state; }
  isAtBase(): boolean { return this.state === 'at_base'; }
  isDead(): boolean { return this.state === 'dead'; }
  getPathProgress(): number { return this.pathProgress; }

  getMovementType(): MonsterMovementType {
    return this.stats.phases[this.currentPhase].movementType;
  }

  static getStats(bossType: BossType): BossStats {
    return { ...BOSS_STATS[bossType] };
  }

  static getAllTypes(): BossType[] {
    return Object.keys(BOSS_STATS) as BossType[];
  }
}
